import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createForm, updateForm, api } from '../../lib/api';
import type { QuizSettings, LiveQuizSettingsConfig } from '../../types/form';

interface CollaboratorPermissions {
  view: boolean;
  edit: boolean;
  full: boolean;
  results: boolean;
  approve: boolean;
}

interface SelectedCollaborator {
  userId?: string;
  email: string;
  displayName?: string | null;
  permissions: CollaboratorPermissions;
  isInvite?: boolean;
}

interface Teammate {
  userId: string;
  email: string;
  displayName: string | null;
  groups: Array<{ id: string; name: string; role: string }>;
}

interface FormCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormType = 'form' | 'poll' | 'quiz' | 'live_quiz';

interface ScheduleSettings {
  enabled: boolean;
  startAt: string;
  endAt: string;
  timezone: string;
  closedMessage: string;
}

interface PollSettings {
  allowMultipleVotes: boolean;
  showResultsAfterVote: boolean;
  anonymousVoting: boolean;
}

interface FormSettings {
  submitButtonText: string;
  successMessage: string;
  theme: 'light' | 'dark' | 'auto';
}

export default function FormCreationWizard({ isOpen, onClose }: FormCreationWizardProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Basic Info
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState<FormType>('form');

  // Step 2: Type-specific settings
  const [quizSettings, setQuizSettings] = useState<QuizSettings>({
    enabled: true,
    showScore: true,
    showScorePercentage: true,
    showCorrectAnswers: true,
    showCorrectAnswersWhen: 'immediately',
    passingScore: 70,
    passingMessage: 'Congratulations! You passed!',
    failingMessage: 'Unfortunately, you did not pass.',
    showPointsPerQuestion: true,
    randomizeQuestions: false,
    randomizeOptions: false,
    allowRetake: true,
    maxAttempts: 0,
  });

  const [pollSettings, setPollSettings] = useState<PollSettings>({
    allowMultipleVotes: false,
    showResultsAfterVote: true,
    anonymousVoting: true,
  });

  // Live Quiz Settings
  const [liveQuizSettings, setLiveQuizSettings] = useState<LiveQuizSettingsConfig>({
    questionTimeSeconds: 20,
    showQuestionOnPlayerDevice: true,
    enableStreakBonus: true,
    streakBonusMultiplier: 1.2,
    streakThreshold: 3,
    timeBasedScoring: true,
    maxPointsPerQuestion: 1000,
    minPointsPerQuestion: 500,
    showLeaderboardAfterQuestion: true,
    leaderboardSize: 5,
    showAnswerDistribution: true,
    showCorrectAnswer: true,
    allowLateJoin: false,
    autoAdvance: false,
    autoAdvanceDelaySeconds: 5,
    randomizeQuestions: false,
    randomizeOptions: true,
    enableSounds: true,
  });

  const [formSettings, setFormSettings] = useState<FormSettings>({
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
    theme: 'light',
  });


  // Step 3: Collaborators
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [teammatesLoading, setTeammatesLoading] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] = useState<SelectedCollaborator[]>([]);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [showCollaboratorDropdown, setShowCollaboratorDropdown] = useState(false);
  const [inviteByEmailMode, setInviteByEmailMode] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  // Step 4: Schedule
  const [schedule, setSchedule] = useState<ScheduleSettings>({
    enabled: false,
    startAt: '',
    endAt: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    closedMessage: 'This form is currently closed.',
  });

  // Approvers (collaborators with approve permission)
  const approvers = selectedCollaborators.filter(c => c.permissions.approve);

  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadTeammates();
    }
  }, [isOpen]);

  function resetForm() {
    setStep(1);
    setFormName('');
    setFormDescription('');
    setFormType('form');
    setQuizSettings({
      enabled: true,
      showScore: true,
      showScorePercentage: true,
      showCorrectAnswers: true,
      showCorrectAnswersWhen: 'immediately',
      passingScore: 70,
      passingMessage: 'Congratulations! You passed!',
      failingMessage: 'Unfortunately, you did not pass.',
      showPointsPerQuestion: true,
      randomizeQuestions: false,
      randomizeOptions: false,
      allowRetake: true,
      maxAttempts: 0,
    });
    setPollSettings({
      allowMultipleVotes: false,
      showResultsAfterVote: true,
      anonymousVoting: true,
    });
    setLiveQuizSettings({
      questionTimeSeconds: 20,
      showQuestionOnPlayerDevice: true,
      enableStreakBonus: true,
      streakBonusMultiplier: 1.2,
      streakThreshold: 3,
      timeBasedScoring: true,
      maxPointsPerQuestion: 1000,
      minPointsPerQuestion: 500,
      showLeaderboardAfterQuestion: true,
      leaderboardSize: 5,
      showAnswerDistribution: true,
      showCorrectAnswer: true,
      allowLateJoin: false,
      autoAdvance: false,
      autoAdvanceDelaySeconds: 5,
      randomizeQuestions: false,
      randomizeOptions: true,
      enableSounds: true,
    });
    setFormSettings({
      submitButtonText: 'Submit',
      successMessage: 'Thank you for your submission!',
      theme: 'light',
    });
    setSelectedCollaborators([]);
    setCollaboratorSearch('');
    setInviteByEmailMode(false);
    setInviteEmail('');
    setSchedule({
      enabled: false,
      startAt: '',
      endAt: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      closedMessage: 'This form is currently closed.',
    });
    setError(null);
  }

  async function loadTeammates() {
    setTeammatesLoading(true);
    try {
      const response = await api.get('/api/user/teammates');
      if (response.ok) {
        const data = await response.json() as { teammates: Teammate[] };
        setTeammates(data.teammates);
      }
    } catch {
      // Ignore errors
    } finally {
      setTeammatesLoading(false);
    }
  }

  function addCollaborator(teammate: Teammate) {
    if (selectedCollaborators.some((c) => c.userId === teammate.userId)) {
      return;
    }
    setSelectedCollaborators([
      ...selectedCollaborators,
      {
        userId: teammate.userId,
        email: teammate.email,
        displayName: teammate.displayName,
        permissions: { view: true, edit: false, full: false, results: false, approve: false },
      },
    ]);
    setCollaboratorSearch('');
    setShowCollaboratorDropdown(false);
  }

  function addCollaboratorByEmail(email: string) {
    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      return;
    }
    if (selectedCollaborators.some((c) => c.email.toLowerCase() === normalizedEmail)) {
      return;
    }
    setSelectedCollaborators([
      ...selectedCollaborators,
      {
        email: normalizedEmail,
        permissions: { view: true, edit: false, full: false, results: false, approve: false },
        isInvite: true,
      },
    ]);
    setInviteEmail('');
    setInviteByEmailMode(false);
  }

  function removeCollaborator(email: string) {
    setSelectedCollaborators(selectedCollaborators.filter((c) => c.email !== email));
  }

  function updateCollaboratorPermission(email: string, permission: keyof CollaboratorPermissions, value: boolean) {
    setSelectedCollaborators(
      selectedCollaborators.map((c) => {
        if (c.email !== email) return c;
        const newPermissions = { ...c.permissions, [permission]: value };
        if (permission === 'full' && value) {
          newPermissions.view = true;
          newPermissions.edit = true;
        }
        if (permission === 'view' && !value) {
          newPermissions.edit = false;
          newPermissions.full = false;
        }
        return { ...c, permissions: newPermissions };
      })
    );
  }

  const filteredTeammates = teammates.filter((t) => {
    const search = collaboratorSearch.toLowerCase();
    if (!search) return true;
    return (
      t.email.toLowerCase().includes(search) ||
      (t.displayName && t.displayName.toLowerCase().includes(search))
    );
  });

  async function handleCreate() {
    if (!formName.trim()) {
      setError('Please enter a form name');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Build initial schema based on form type
      const initialSchema: Record<string, unknown> = {
        version: 1,
        fields: [],
        steps: [],
        conditionalLogic: [],
        settings: {
          submitButtonText: formSettings.submitButtonText,
          successMessage: formSettings.successMessage,
          theme: formSettings.theme,
          submitActions: [{ type: 'database' }],
        },
      };

      // Add form type specific settings
      if (formType === 'poll') {
        initialSchema.pollSettings = {
          enabled: true,
          ...pollSettings,
        };
      } else if (formType === 'quiz') {
        // Set quiz settings in the correct location (settings.quiz)
        (initialSchema.settings as Record<string, unknown>).quiz = quizSettings;
      } else if (formType === 'live_quiz') {
        // Set live quiz settings
        (initialSchema.settings as Record<string, unknown>).quiz = {
          enabled: true,
          mode: 'live',
          showScore: true,
          showScorePercentage: true,
          showCorrectAnswers: true,
          showCorrectAnswersWhen: 'immediately',
          passingScore: 0,
          showPointsPerQuestion: true,
          randomizeQuestions: liveQuizSettings.randomizeQuestions,
          randomizeOptions: liveQuizSettings.randomizeOptions,
          allowRetake: false,
          maxAttempts: 1,
          liveSettings: liveQuizSettings,
        };
      }

      // Add schedule settings if enabled
      if (schedule.enabled && (schedule.startAt || schedule.endAt)) {
        initialSchema.scheduleSettings = {
          enabled: true,
          startAt: schedule.startAt || null,
          endAt: schedule.endAt || null,
          timezone: schedule.timezone,
          closedMessage: schedule.closedMessage,
        };
      }

      // Add approval settings if there are approvers
      if (approvers.length > 0) {
        initialSchema.approvalSettings = {
          enabled: true,
          requireApprovalBeforePublish: true,
          approvers: approvers.map(a => a.email),
        };
      }

      // Create the form first
      const result = await createForm({
        name: formName.trim(),
        description: formDescription.trim() || undefined,
      });

      // Update form with initial schema settings
      await updateForm(result.id, {
        schema: initialSchema as unknown as Parameters<typeof updateForm>[1]['schema'],
      });

      // Add collaborators if any selected
      if (selectedCollaborators.length > 0) {
        const collaboratorsPayload = selectedCollaborators.map((c) => ({
          email: c.email,
          permissions: c.permissions,
        }));

        await api.post(`/api/forms/${result.id}/collaborators`, {
          collaborators: collaboratorsPayload,
        });
      }

      onClose();
      // Navigate to the appropriate builder based on form type
      if (formType === 'live_quiz') {
        navigate(`/formspace/live-quiz/${result.id}`);
      } else {
        navigate(`/formspace/builder/${result.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create form');
    } finally {
      setCreating(false);
    }
  }

  function canProceed(): boolean {
    if (step === 1) {
      return formName.trim().length > 0;
    }
    return true;
  }

  function getStepTitle(stepNum: number): string {
    switch (stepNum) {
      case 1: return 'Basics';
      case 2: return 'Settings';
      case 3: return 'Team';
      case 4: return 'Schedule';
      default: return '';
    }
  }

  if (!isOpen) return null;

  const totalSteps = 4;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Form</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s < step
                      ? 'bg-green-500 text-white'
                      : s === step
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {s < step ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    s
                  )}
                </div>
                {s < totalSteps && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      s < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex mt-2 text-xs text-gray-500 dark:text-gray-400">
            {[1, 2, 3, 4].map((s) => (
              <span key={s} className="flex-1 text-center">{getStepTitle(s)}</span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Basic Info & Form Type */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Form Name *
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="input w-full"
                  placeholder="e.g., Customer Feedback Survey"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="input w-full"
                  rows={2}
                  placeholder="What is this form for?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Form Type
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Regular Form */}
                  <button
                    type="button"
                    onClick={() => setFormType('form')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formType === 'form'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-200 dark:ring-primary-800'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      formType === 'form' ? 'bg-primary-100 dark:bg-primary-900/50' : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <svg className={`w-5 h-5 ${formType === 'form' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className={`font-medium ${formType === 'form' ? 'text-primary-900 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                      Form
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Collect data and submissions
                    </p>
                  </button>

                  {/* Poll */}
                  <button
                    type="button"
                    onClick={() => setFormType('poll')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formType === 'poll'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 ring-2 ring-purple-200 dark:ring-purple-800'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      formType === 'poll' ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <svg className={`w-5 h-5 ${formType === 'poll' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className={`font-medium ${formType === 'poll' ? 'text-purple-900 dark:text-purple-300' : 'text-gray-900 dark:text-white'}`}>
                      Poll
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Quick voting and results
                    </p>
                  </button>

                  {/* Quiz */}
                  <button
                    type="button"
                    onClick={() => setFormType('quiz')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formType === 'quiz'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 ring-2 ring-green-200 dark:ring-green-800'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      formType === 'quiz' ? 'bg-green-100 dark:bg-green-900/50' : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <svg className={`w-5 h-5 ${formType === 'quiz' ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <h3 className={`font-medium ${formType === 'quiz' ? 'text-green-900 dark:text-green-300' : 'text-gray-900 dark:text-white'}`}>
                      Quiz
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Self-paced knowledge test
                    </p>
                  </button>

                  {/* Live Quiz */}
                  <button
                    type="button"
                    onClick={() => setFormType('live_quiz')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formType === 'live_quiz'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 ring-2 ring-orange-200 dark:ring-orange-800'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                      formType === 'live_quiz' ? 'bg-orange-100 dark:bg-orange-900/50' : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <svg className={`w-5 h-5 ${formType === 'live_quiz' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-medium ${formType === 'live_quiz' ? 'text-orange-900 dark:text-orange-300' : 'text-gray-900 dark:text-white'}`}>
                        Live Quiz
                      </h3>
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200 rounded">
                        Interactive
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Real-time hosted game
                    </p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Type-specific Settings */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Quiz Settings */}
              {formType === 'quiz' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quiz Settings</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Configure how your quiz works</p>
                    </div>
                  </div>

                  {/* Score Display */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Score Display</h4>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={quizSettings.showScore}
                        onChange={(e) => setQuizSettings({ ...quizSettings, showScore: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show score after submission</span>
                    </label>
                    {quizSettings.showScore && (
                      <label className="flex items-center gap-3 ml-6">
                        <input
                          type="checkbox"
                          checked={quizSettings.showScorePercentage}
                          onChange={(e) => setQuizSettings({ ...quizSettings, showScorePercentage: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Show percentage</span>
                      </label>
                    )}
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={quizSettings.showPointsPerQuestion}
                        onChange={(e) => setQuizSettings({ ...quizSettings, showPointsPerQuestion: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show points per question</span>
                    </label>
                  </div>

                  {/* Correct Answers */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Correct Answers</h4>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={quizSettings.showCorrectAnswers}
                        onChange={(e) => setQuizSettings({ ...quizSettings, showCorrectAnswers: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show correct answers</span>
                    </label>
                    {quizSettings.showCorrectAnswers && (
                      <div className="ml-6">
                        <label className="text-xs text-gray-600 dark:text-gray-400">When to show:</label>
                        <select
                          value={quizSettings.showCorrectAnswersWhen}
                          onChange={(e) => setQuizSettings({ ...quizSettings, showCorrectAnswersWhen: e.target.value as 'immediately' | 'after_deadline' | 'never' })}
                          className="mt-1 w-full input"
                        >
                          <option value="immediately">Immediately after submission</option>
                          <option value="after_deadline">After deadline passes</option>
                          <option value="never">Never show</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Passing Score */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Passing Score</h4>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={quizSettings.passingScore || 0}
                        onChange={(e) => setQuizSettings({ ...quizSettings, passingScore: Number(e.target.value) })}
                        className="w-20 input"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">% to pass</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Set to 0 for no passing requirement</p>

                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Passing message:</label>
                        <input
                          type="text"
                          value={quizSettings.passingMessage || ''}
                          onChange={(e) => setQuizSettings({ ...quizSettings, passingMessage: e.target.value })}
                          className="mt-1 w-full input text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Failing message:</label>
                        <input
                          type="text"
                          value={quizSettings.failingMessage || ''}
                          onChange={(e) => setQuizSettings({ ...quizSettings, failingMessage: e.target.value })}
                          className="mt-1 w-full input text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Randomization & Retakes */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Randomization</h4>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={quizSettings.randomizeQuestions}
                          onChange={(e) => setQuizSettings({ ...quizSettings, randomizeQuestions: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Shuffle questions</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={quizSettings.randomizeOptions}
                          onChange={(e) => setQuizSettings({ ...quizSettings, randomizeOptions: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Shuffle options</span>
                      </label>
                    </div>

                    <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Retakes</h4>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={quizSettings.allowRetake}
                          onChange={(e) => setQuizSettings({ ...quizSettings, allowRetake: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-green-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Allow retakes</span>
                      </label>
                      {quizSettings.allowRetake && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            value={quizSettings.maxAttempts || 0}
                            onChange={(e) => setQuizSettings({ ...quizSettings, maxAttempts: Number(e.target.value) })}
                            className="w-16 input text-sm"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400">max (0 = unlimited)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Poll Settings */}
              {formType === 'poll' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Poll Settings</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Configure how your poll works</p>
                    </div>
                  </div>

                  <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Allow Multiple Votes</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Let users vote more than once</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={pollSettings.allowMultipleVotes}
                        onChange={(e) => setPollSettings({ ...pollSettings, allowMultipleVotes: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
                      />
                    </label>

                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show Results After Vote</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Display results immediately after voting</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={pollSettings.showResultsAfterVote}
                        onChange={(e) => setPollSettings({ ...pollSettings, showResultsAfterVote: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
                      />
                    </label>

                    <label className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Anonymous Voting</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Don't track who voted for what</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={pollSettings.anonymousVoting}
                        onChange={(e) => setPollSettings({ ...pollSettings, anonymousVoting: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-purple-600 dark:bg-gray-700"
                      />
                    </label>
                  </div>
                </>
              )}

              {/* Regular Form Settings */}
              {formType === 'form' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Form Settings</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Configure how your form looks and behaves</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Submit Button Text
                      </label>
                      <input
                        type="text"
                        value={formSettings.submitButtonText}
                        onChange={(e) => setFormSettings({ ...formSettings, submitButtonText: e.target.value })}
                        className="input w-full"
                        placeholder="Submit"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Success Message
                      </label>
                      <textarea
                        value={formSettings.successMessage}
                        onChange={(e) => setFormSettings({ ...formSettings, successMessage: e.target.value })}
                        className="input w-full"
                        rows={2}
                        placeholder="Thank you for your submission!"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Theme
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {(['light', 'dark', 'auto'] as const).map((theme) => (
                          <button
                            key={theme}
                            type="button"
                            onClick={() => setFormSettings({ ...formSettings, theme })}
                            className={`p-3 rounded-lg border-2 text-center transition-all ${
                              formSettings.theme === theme
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <span className={`text-sm font-medium capitalize ${
                              formSettings.theme === theme
                                ? 'text-primary-700 dark:text-primary-300'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {theme}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Live Quiz Settings */}
              {formType === 'live_quiz' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                      <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Quiz Settings</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Configure your live quiz game experience</p>
                    </div>
                  </div>

                  {/* Timing Settings */}
                  <div className="space-y-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h4 className="text-sm font-medium text-orange-800 dark:text-orange-300">Timing</h4>
                    <div className="flex items-center gap-3">
                      <label className="text-sm text-gray-700 dark:text-gray-300">Time per question:</label>
                      <input
                        type="number"
                        min={5}
                        max={120}
                        value={liveQuizSettings.questionTimeSeconds}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, questionTimeSeconds: Number(e.target.value) })}
                        className="w-20 input text-sm"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">seconds</span>
                    </div>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.timeBasedScoring}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, timeBasedScoring: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Award more points for faster answers</span>
                    </label>
                  </div>

                  {/* Scoring Settings */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Scoring</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Max points per question:</label>
                        <input
                          type="number"
                          min={100}
                          max={5000}
                          step={100}
                          value={liveQuizSettings.maxPointsPerQuestion}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, maxPointsPerQuestion: Number(e.target.value) })}
                          className="mt-1 w-full input text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 dark:text-gray-400">Min points per question:</label>
                        <input
                          type="number"
                          min={0}
                          max={liveQuizSettings.maxPointsPerQuestion}
                          step={100}
                          value={liveQuizSettings.minPointsPerQuestion}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, minPointsPerQuestion: Number(e.target.value) })}
                          className="mt-1 w-full input text-sm"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.enableStreakBonus}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, enableStreakBonus: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Enable streak bonus (consecutive correct answers)</span>
                    </label>
                  </div>

                  {/* Display Settings */}
                  <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Display</h4>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.showQuestionOnPlayerDevice}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, showQuestionOnPlayerDevice: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show question text on player devices</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.showLeaderboardAfterQuestion}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, showLeaderboardAfterQuestion: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show leaderboard after each question</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.showAnswerDistribution}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, showAnswerDistribution: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show answer distribution chart</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={liveQuizSettings.showCorrectAnswer}
                        onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, showCorrectAnswer: e.target.checked })}
                        className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Reveal correct answer after each question</span>
                    </label>
                  </div>

                  {/* Game Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Randomization</h4>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={liveQuizSettings.randomizeQuestions}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, randomizeQuestions: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Shuffle questions</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={liveQuizSettings.randomizeOptions}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, randomizeOptions: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Shuffle options</span>
                      </label>
                    </div>

                    <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Other</h4>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={liveQuizSettings.allowLateJoin}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, allowLateJoin: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Allow late join</span>
                      </label>
                      <label className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={liveQuizSettings.enableSounds}
                          onChange={(e) => setLiveQuizSettings({ ...liveQuizSettings, enableSounds: e.target.checked })}
                          className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:bg-gray-700"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Enable sounds</span>
                      </label>
                    </div>
                  </div>

                  {/* How it works info */}
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">How Live Quiz Works</h4>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                          After creating and publishing, you'll get a unique Game PIN. Share this PIN with participants who can join at the /play page. You control the game flow from the host dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>
          )}

          {/* Step 3: Collaborators & Approvers */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Collaborators</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Add team members who can view or edit this form</p>
                  </div>
                </div>

                {/* Selected Collaborators */}
                {selectedCollaborators.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {selectedCollaborators.map((collab) => (
                      <div
                        key={collab.email}
                        className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 text-sm font-medium">
                              {(collab.displayName || collab.email)[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {collab.displayName || collab.email}
                              {collab.isInvite && (
                                <span className="ml-1 text-xs text-orange-600">(will be invited)</span>
                              )}
                            </p>
                            {collab.displayName && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{collab.email}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <div className="flex gap-1 flex-wrap">
                            <button
                              type="button"
                              onClick={() => updateCollaboratorPermission(collab.email, 'view', !collab.permissions.view)}
                              className={`px-2 py-1 text-xs rounded ${
                                collab.permissions.view
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                              }`}
                              title="View form"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => updateCollaboratorPermission(collab.email, 'edit', !collab.permissions.edit)}
                              className={`px-2 py-1 text-xs rounded ${
                                collab.permissions.edit
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                              }`}
                              title="Edit form"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => updateCollaboratorPermission(collab.email, 'results', !collab.permissions.results)}
                              className={`px-2 py-1 text-xs rounded ${
                                collab.permissions.results
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                              }`}
                              title="View results"
                            >
                              Results
                            </button>
                            <button
                              type="button"
                              onClick={() => updateCollaboratorPermission(collab.email, 'approve', !collab.permissions.approve)}
                              className={`px-2 py-1 text-xs rounded ${
                                collab.permissions.approve
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                              }`}
                              title="Approver (must approve before publish)"
                            >
                              Approver
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeCollaborator(collab.email)}
                            className="text-gray-400 hover:text-red-500 ml-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Collaborator */}
                {!inviteByEmailMode ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={collaboratorSearch}
                      onChange={(e) => {
                        setCollaboratorSearch(e.target.value);
                        setShowCollaboratorDropdown(true);
                      }}
                      onFocus={() => setShowCollaboratorDropdown(true)}
                      className="input w-full pr-10"
                      placeholder={teammates.length > 0 ? "Search teammates..." : "No teammates yet"}
                    />
                    <svg
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>

                    {showCollaboratorDropdown && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {teammatesLoading ? (
                          <div className="p-3 text-center text-gray-500">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                          </div>
                        ) : filteredTeammates.length > 0 ? (
                          <>
                            {filteredTeammates
                              .filter((t) => !selectedCollaborators.some((c) => c.userId === t.userId))
                              .map((teammate) => (
                                <button
                                  key={teammate.userId}
                                  type="button"
                                  onClick={() => addCollaborator(teammate)}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-blue-600 text-sm font-medium">
                                      {(teammate.displayName || teammate.email)[0].toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {teammate.displayName || teammate.email}
                                    </p>
                                    {teammate.displayName && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{teammate.email}</p>
                                    )}
                                  </div>
                                </button>
                              ))}
                          </>
                        ) : (
                          <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                            No teammates found
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setInviteByEmailMode(true);
                            setShowCollaboratorDropdown(false);
                            setInviteEmail(collaboratorSearch);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700 text-blue-600 dark:text-blue-400"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                          <span className="text-sm">Invite by email</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCollaboratorByEmail(inviteEmail);
                        }
                      }}
                      className="input flex-1"
                      placeholder="Enter email address..."
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => addCollaboratorByEmail(inviteEmail)}
                      className="btn-primary px-3"
                      disabled={!inviteEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail)}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInviteByEmailMode(false);
                        setInviteEmail('');
                      }}
                      className="btn-secondary px-3"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {showCollaboratorDropdown && (
                  <div
                    className="fixed inset-0 z-0"
                    onClick={() => setShowCollaboratorDropdown(false)}
                  ></div>
                )}
              </div>

              {/* Approvers Info */}
              {approvers.length > 0 && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">Approval Required</h4>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        {approvers.length} approver{approvers.length > 1 ? 's' : ''} must approve before the form can be published.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                You can skip this step and add collaborators later from the form builder.
              </p>
            </div>
          )}

          {/* Step 4: Schedule */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Schedule Form Availability</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Set when the form opens and closes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={schedule.enabled}
                    onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {schedule.enabled && (
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Start Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={schedule.startAt}
                        onChange={(e) => setSchedule({ ...schedule, startAt: e.target.value })}
                        className="input w-full"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave empty for immediate availability</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={schedule.endAt}
                        onChange={(e) => setSchedule({ ...schedule, endAt: e.target.value })}
                        className="input w-full"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Leave empty for no deadline</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Timezone
                    </label>
                    <select
                      value={schedule.timezone}
                      onChange={(e) => setSchedule({ ...schedule, timezone: e.target.value })}
                      className="input w-full"
                    >
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">London (GMT/BST)</option>
                      <option value="Europe/Paris">Central European Time</option>
                      <option value="Asia/Tokyo">Japan Standard Time</option>
                      <option value="Asia/Shanghai">China Standard Time</option>
                      <option value="Australia/Sydney">Australian Eastern Time</option>
                      <option value="Asia/Jerusalem">Israel Time</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Closed Message
                    </label>
                    <input
                      type="text"
                      value={schedule.closedMessage}
                      onChange={(e) => setSchedule({ ...schedule, closedMessage: e.target.value })}
                      className="input w-full"
                      placeholder="Message shown when form is closed"
                    />
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                You can skip this step and configure scheduling later from the form builder.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="btn-secondary"
            disabled={creating}
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </button>

          <div className="flex gap-3">
            {step < totalSteps && (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="btn-secondary"
                disabled={!canProceed()}
              >
                Skip
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (step < totalSteps) {
                  setStep(step + 1);
                } else {
                  handleCreate();
                }
              }}
              className="btn-primary"
              disabled={creating || !canProceed()}
            >
              {creating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : step < totalSteps ? (
                'Continue'
              ) : (
                'Create Form'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
